/* Henry — chorégraphie (GSAP + ScrollTrigger + Lenis).
   Inactif si « réduire les animations » est activé ou si les librairies ne chargent pas :
   rien n'est masqué en CSS, le site reste entièrement lisible sans ce fichier. */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || window.Shopify?.designMode || !window.gsap || !window.ScrollTrigger) return; // éditeur de thème : pas d'animations (sections rechargées à chaud)
  gsap.registerPlugin(ScrollTrigger);
  document.documentElement.classList.add('gsap-on');

  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  const fine = matchMedia('(pointer: fine)').matches;
  const EASE = 'expo.out';
  const skip = el => el.closest('.drawer, .cart, .search, .mega, .menu, [hidden]');

  /* ---------- Défilement doux (souris / trackpad uniquement) ---------- */
  let lenis = null;
  if (window.Lenis && fine) {
    // Les panneaux (panier, tiroirs, recherche, menu) gardent leur propre défilement, même quand Lenis est en pause
    lenis = new Lenis({ lerp: 0.1, smoothWheel: true, prevent: node => !!node.closest?.('[data-lenis-prevent], .drawer, .cart, .search, .menu') });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(t => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
    $$('a[href^="#"]').forEach(a => a.addEventListener('click', e => {
      const id = a.getAttribute('href');
      if (id.length > 1 && $(id)) { e.preventDefault(); lenis.scrollTo(id, { offset: -80 }); }
    }));
    // Les panneaux modaux bloquent le défilement du document : Lenis se met en pause avec eux
    new MutationObserver(() => (document.body.style.overflow === 'hidden' ? lenis.stop() : lenis.start()))
      .observe(document.body, { attributes: true, attributeFilter: ['style'] });
  }

  /* ---------- Découpage en mots (titres courts uniquement) ---------- */
  const split = el => {
    if (el.dataset.split) return $$('.wi', el);
    const words = [];
    const walk = node => [...node.childNodes].forEach(n => {
      if (n.nodeType === 3) {
        const frag = document.createDocumentFragment();
        n.textContent.split(/(\s+)/).forEach(p => {
          if (!p) return;
          if (/^\s+$/.test(p)) return frag.appendChild(document.createTextNode(p));
          const o = document.createElement('span'), i = document.createElement('span');
          o.className = 'w'; i.className = 'wi'; i.textContent = p;
          o.appendChild(i); frag.appendChild(o); words.push(i);
        });
        n.replaceWith(frag);
      } else if (n.nodeType === 1 && !n.matches('svg, br, .icon')) walk(n);
    });
    walk(el); el.dataset.split = '1';
    return words;
  };

  const introDelay = 0.15;

  /* ---------- Hero et bandeaux : entrée chorégraphiée + sortie au défilement ---------- */
  // Chaque section est enveloppée par Shopify (#shopify-section-…) : on cherche le bandeau de la première section
  const hero = $('.vhero') || $('main > .shopify-section:first-child .band, main > .band');
  if (hero) {
    const lines = $$('.poster > span', hero).flatMap(split);
    const tl = gsap.timeline({ delay: introDelay });
    tl.from($$('.label, .crumbs', hero), { opacity: 0, y: 14, duration: 1, ease: EASE })
      .from(lines, { yPercent: 118, duration: 1.5, ease: EASE, stagger: 0.07 }, '-=0.7')
      .from($$('.text, .ctas, .promise, .vctrl', hero), { opacity: 0, y: 18, duration: 1.1, ease: EASE, stagger: 0.08 }, '-=1.1');
    const inner = $('.vhero-inner, .container', hero);
    gsap.to(inner, { yPercent: -16, opacity: 0.1, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true } });
    const media = $('video', hero) || $(':scope > img', hero);
    if (media) gsap.fromTo(media, { scale: 1.1 }, { scale: 1, yPercent: 10, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true } });
  }

  /* ---------- Apparitions « une fois » : déclenchées par IntersectionObserver (insensible aux changements de hauteur de page,
     contrairement aux positions ScrollTrigger calculées au chargement). Un élément déjà dépassé est affiché tout de suite. ---------- */
  const onEnter = (el, play) => {
    if (!('IntersectionObserver' in window)) { play(); return; }
    const io = new IntersectionObserver(entries => entries.forEach(e => {
      if (e.isIntersecting || e.boundingClientRect.top < 0) { io.disconnect(); play(); }
    }), { rootMargin: '0px 0px -8% 0px', threshold: 0 });
    io.observe(el);
  };

  /* ---------- Titres : les mots montent depuis un masque ---------- */
  $$('main .title, main .poster > span:not(.script), footer .news-sign, main .u-title, .pdp-info h1').forEach(el => {
    if (skip(el) || (hero && hero.contains(el))) return;
    const w = split(el);
    const tw = gsap.from(w, { yPercent: 118, duration: 1.2, ease: EASE, stagger: Math.min(0.06, 0.5 / w.length), paused: true });
    onEnter(el, () => tw.play());
  });

  /* ---------- Manifeste : les mots s'allument au fil du défilement ---------- */
  $$('[data-scrub]').forEach(el => {
    const w = split(el);
    gsap.fromTo(w, { opacity: 0.14 }, { opacity: 1, stagger: 0.1, ease: 'none', scrollTrigger: { trigger: el, start: 'top 82%', end: 'bottom 50%', scrub: true } });
  });

  /* ---------- Images : rideau + dézoom ---------- */
  const MEDIA = '.g-item, .card-media, .acard-media, .p-media, .a-img, .g-main, .a-poster-main, .story-media, .gift-visual, .gs-visual, .svc-card img, .masonry figure, .private img, .deco-row .par-media, .mosaic img';
  $$(MEDIA).forEach(m => {
    if (skip(m) || (hero && hero.contains(m))) return;
    const img = m.tagName === 'IMG' ? null : $('img', m);
    const tl = gsap.timeline({ paused: true,
      onComplete: () => { gsap.set(m, { clearProps: 'clipPath' }); if (img && !img.hasAttribute('data-parallax')) gsap.set(img, { clearProps: 'transform' }); } });
    // Cartes produit : apparition discrète (léger dézoom, sans rideau) ; grands visuels : rideau + dézoom
    const card = m.matches('.p-media, .card-media, .acard-media, .a-img');
    if (!card) tl.fromTo(m, { clipPath: 'inset(16% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.5, ease: EASE });
    if (img && !img.hasAttribute('data-parallax')) tl.fromTo(img, { scale: card ? 1.04 : 1.16 }, { scale: 1, duration: card ? 0.9 : 1.9, ease: EASE }, 0);
    onEnter(m, () => tl.play());
  });

  /* ---------- Parallaxe ---------- */
  $$('[data-parallax]').forEach(img => {
    const amt = +img.dataset.parallax || 10;
    gsap.fromTo(img, { yPercent: -amt }, { yPercent: amt, ease: 'none', scrollTrigger: { trigger: img.parentElement, start: 'top bottom', end: 'bottom top', scrub: true } });
  });

  /* ---------- Groupes : apparition en cascade (8 éléments max visibles) ---------- */
  const GROUPS = '.grid-p, .posters, .values, .reassure, .steps, .svc, .facts-strip, .svc-grid, .occasions, .trust, .shop-rail, .deco-row';
  const grouped = new Set();
  $$(GROUPS).forEach(g => {
    if (skip(g)) return;
    const kids = [...g.children].filter(k => !k.hidden && !k.classList.contains('occ-float'));
    if (!kids.length) return;
    kids.forEach(k => grouped.add(k));
    const tw = gsap.from(kids, { opacity: 0, y: 12, duration: 0.7, ease: EASE, stagger: Math.min(0.05, 0.4 / kids.length), paused: true, clearProps: 'opacity,transform' });
    onEnter(g, () => tw.play());
  });

  /* ---------- Blocs .reveal restants ---------- */
  $$('.reveal').forEach(el => {
    if (skip(el) || grouped.has(el) || (hero && hero.contains(el))) return;
    const tw = gsap.from(el, { opacity: 0, y: 26, duration: 1.1, ease: EASE, paused: true, clearProps: 'opacity,transform' });
    onEnter(el, () => tw.play());
  });

  /* ---------- Ateliers : défilement horizontal épinglé (desktop) ---------- */
  const mm = gsap.matchMedia();
  mm.add('(min-width: 1100px)', () => {
    const sec = $('.at-sec'), rail = sec && $('.at-rail', sec);
    if (!rail) return;
    sec.classList.add('pinned');
    const bar = $('[data-rail-progress]', sec);
    const dist = () => Math.max(0, rail.scrollWidth - rail.clientWidth);
    const tween = gsap.to(rail, { x: () => -dist(), ease: 'none',
      scrollTrigger: { trigger: sec, start: 'top top', end: () => '+=' + dist(), pin: true, scrub: 0.6, invalidateOnRefresh: true,
        onUpdate: s => bar && bar.style.setProperty('--p', (0.2 + s.progress * 0.8).toFixed(3)) } });
    // Navigation clavier : le focus sur un atelier fait défiler jusqu'à lui
    const onFocus = e => {
      const card = e.target.closest('.acard'); if (!card) return;
      const st = tween.scrollTrigger, r = Math.min(1, card.offsetLeft / Math.max(1, dist()));
      const y = st.start + r * (st.end - st.start);
      lenis ? lenis.scrollTo(y, { immediate: true }) : scrollTo(0, y);
    };
    rail.addEventListener('focusin', onFocus);
    return () => { sec.classList.remove('pinned'); rail.removeEventListener('focusin', onFocus); };
  });

  addEventListener('load', () => ScrollTrigger.refresh());
  if (window.ResizeObserver) { let t, h = 0; new ResizeObserver(() => { const nh = document.body.scrollHeight; if (Math.abs(nh - h) < 40) return; h = nh; clearTimeout(t); t = setTimeout(() => { try { ScrollTrigger.refresh(); } catch (e) {} }, 300); }).observe(document.body); }
})();
